import React, { useMemo, useState } from 'react';

// Normalize a label into a safe camelCase field name
function normalizeFieldName(label) {
    if (!label) return '';
    // remove characters that are not letters/numbers/space/-/_
    const cleaned = label.replace(/[^a-zA-Z0-9 _-]/g, ' ').trim();
    const parts = cleaned.split(/[_\-\s]+/).filter(Boolean);
    if (parts.length === 0) return '';
    return parts.map((p, idx) => {
        const lower = p.toLowerCase();
        if (idx === 0) return lower;
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }).join('');
}

// Basic text input renderer that follows existing design system
function TextInput({ name, label, type = 'text', value, onChange, required, disabled }) {
    return (
        <div>
            <label className="sr-only" htmlFor={name}>{label}</label>
            <input
                id={name}
                name={name}
                type={type}
                placeholder={label}
                value={value}
                onChange={onChange}
                required={required}
                disabled={disabled}
                className={`w-full px-4 py-3 md:py-4 h-12 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-blue-500 ${disabled ? 'bg-stone-100 cursor-not-allowed' : ''}`}
            />
        </div>
    );
}

function toStartCase(str) {
    if (!str) return '';
    return String(str)
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_\-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
        .join(' ');
}

export default function DynamicForm({
    customFields = [],
    defaultFields = [],
    initialValues = {},
    onSubmit,
    submitLabel = 'Submit',
    includeDefaults = true,
    className = '',
    disabled = false,
}) {
    // Expect customFields to be normalized FormField objects: { id, label, type, required, editable, locked }
    const dynamicDefs = useMemo(() => {
        const defs = [];
        if (includeDefaults && Array.isArray(defaultFields)) defs.push(...defaultFields);
        if (Array.isArray(customFields)) {
            // If entries already have `id` and `label` assume normalized; otherwise attempt to normalize
            customFields.forEach(cf => {
                if (!cf) return;
                if (typeof cf === 'string') {
                    const label = String(cf).trim();
                    if (!label) return;
                    defs.push({ id: normalizeFieldName(label), label, type: 'text', required: false, editable: true, locked: false });
                } else if (cf.id && cf.label) {
                    defs.push({ id: cf.id, label: cf.label, type: cf.type || 'text', required: !!cf.required, editable: cf.editable !== undefined ? !!cf.editable : true, locked: !!cf.locked, options: cf.options });
                } else {
                    const raw = cf.name || cf.label || cf.value || cf.id || '';
                    const label = cf.label ? String(cf.label).trim() : String(raw).trim();
                    const id = cf.id ? String(cf.id).trim() : normalizeFieldName(label);
                    if (!id) return;
                    defs.push({ id, label: toStartCase ? toStartCase(label) : label, type: cf.type || 'text', required: !!cf.required, editable: cf.editable !== undefined ? !!cf.editable : true, locked: !!cf.locked, options: cf.options });
                }
            });
        }
        return defs;
    }, [customFields, defaultFields, includeDefaults]);

    const [values, setValues] = useState(() => {
        const start = {};
        dynamicDefs.forEach(f => {
            const id = f.id || f.name;
            if (f.type === 'multiselect') {
                // Multi-select: initialValues can be array or single value
                const init = initialValues[id];
                start[id] = Array.isArray(init) ? init : (init ? [init] : []);
            } else {
                start[id] = initialValues[id] ?? '';
            }
        });
        return start;
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues(prev => ({ ...prev, [name]: value }));
    };

    const handleMultiselectToggle = (fieldId, optionValue) => {
        setValues(prev => {
            const current = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];
            const next = current.includes(optionValue)
                ? current.filter(v => v !== optionValue)
                : [...current, optionValue];
            return { ...prev, [fieldId]: next };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Build responses mapping keyed by field id
        const responses = {};
        dynamicDefs.forEach(f => {
            const id = f.id || f.name;
            const val = values[id] ?? '';
            responses[id] = val;
            // For multiselect fields, also store a human-readable display version
            if (f.type === 'multiselect' && Array.isArray(val) && val.length > 0 && f.options) {
                const optMap = Object.fromEntries((f.options || []).map(o => [o.value, o.label]));
                responses[`${id}Display`] = val.map(v => optMap[v] || v).join(', ');
            }
        });
        if (typeof onSubmit === 'function') onSubmit({ responses });
    };

    return (
        <form onSubmit={handleSubmit} className={className}>
            <div className="space-y-4">
                {dynamicDefs.map(field => {
                    const id = field.id || field.name;
                    const label = field.label || id;
                    const type = field.type || 'text';
                    const required = !!field.required;
                    const editable = field.editable !== undefined ? !!field.editable : true;
                    return (
                        <div key={id}>
                            {type === 'multiselect' ? (
                                <div>
                                    <label className="block text-xs font-medium text-stone-500 mb-2 px-1">{label}</label>
                                    <div className={`w-full border border-stone-300 rounded-lg overflow-hidden ${(disabled || !editable) ? 'bg-stone-100 cursor-not-allowed' : 'bg-white'}`}>
                                        {(field.options || []).length === 0 ? (
                                            <p className="px-4 py-3 text-sm text-stone-400">No options available</p>
                                        ) : (
                                            <div className="max-h-48 overflow-y-auto">
                                                {(field.options || []).map(opt => {
                                                    const selected = (Array.isArray(values[id]) ? values[id] : []).includes(opt.value);
                                                    return (
                                                        <label
                                                            key={opt.value}
                                                            className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors border-b border-stone-100 last:border-b-0 ${selected ? 'bg-blue-50' : 'hover:bg-stone-50'
                                                                } ${(disabled || !editable) ? 'pointer-events-none opacity-60' : ''}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selected}
                                                                onChange={() => handleMultiselectToggle(id, opt.value)}
                                                                disabled={disabled || !editable}
                                                                className="w-4 h-4 rounded border-stone-300 text-blue-600 focus:ring-blue-500"
                                                            />
                                                            <span className="text-sm text-stone-700">{opt.label}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                    {required && Array.isArray(values[id]) && values[id].length === 0 && (
                                        <input
                                            tabIndex={-1}
                                            autoComplete="off"
                                            style={{ opacity: 0, height: 0, position: 'absolute' }}
                                            value=""
                                            required
                                            onChange={() => { }}
                                        />
                                    )}
                                </div>
                            ) : type === 'select' ? (
                                <div>
                                    <label className="sr-only" htmlFor={id}>{label}</label>
                                    <select
                                        id={id}
                                        name={id}
                                        value={values[id] ?? ''}
                                        onChange={handleChange}
                                        required={required}
                                        disabled={disabled || !editable}
                                        className={`w-full px-4 py-3 md:py-4 h-12 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white ${(disabled || !editable) ? 'bg-stone-100 cursor-not-allowed' : ''} ${!values[id] ? 'text-stone-400' : 'text-stone-900'}`}
                                    >
                                        <option value="">{label}</option>
                                        {(field.options || []).map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ) : type === 'textarea' ? (
                                <textarea
                                    id={id}
                                    name={id}
                                    placeholder={label}
                                    value={values[id] ?? ''}
                                    onChange={handleChange}
                                    required={required}
                                    disabled={disabled || !editable}
                                    rows={4}
                                    className={`w-full px-4 py-3 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-blue-500 ${(disabled || !editable) ? 'bg-stone-100 cursor-not-allowed' : ''}`}
                                />
                            ) : (
                                <TextInput
                                    name={id}
                                    label={label}
                                    type={type}
                                    value={values[id] ?? ''}
                                    onChange={handleChange}
                                    required={required}
                                    disabled={disabled || !editable}
                                />
                            )}
                        </div>
                    );
                })}

                <button
                    type="submit"
                    disabled={disabled}
                    className={`w-full bg-black text-white py-3 rounded-lg font-semibold transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-stone-900'}`}
                >
                    {submitLabel}
                </button>
            </div>
        </form>
    );
}
